import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "../modules/auth/AuthContext";
import { apiBaseUrl } from "../lib/api";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { useThemeMode } from "../hooks/useThemeMode";

function Icon({ name }: { name: "menu" | "dashboard" | "clients" | "progress" | "checkins" | "routines" | "nutrition" | "reports" | "settings" | "bell" | "sun" | "moon" | "search" | "chevron" | "logout" | "messages" | "profile" }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const paths = {
    menu: <><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></>,
    dashboard: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></>,
    clients: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    progress: <><path d="M4 19V9" /><path d="M10 19V5" /><path d="M16 19v-8" /><path d="M22 19H2" /></>,
    checkins: <><rect x="4" y="4" width="16" height="18" rx="2" /><path d="M8 2v4" /><path d="M16 2v4" /><path d="m8 14 3 3 5-6" /></>,
    routines: <><path d="M6 6v12" /><path d="M18 6v12" /><path d="M3 9v6" /><path d="M21 9v6" /><path d="M6 12h12" /></>,
    nutrition: <><path d="M12 6c-3 0-5 2-5 6 0 5 3 8 5 8s5-3 5-8c0-4-2-6-5-6Z" /><path d="M12 6c0-2 1-3 3-4" /><path d="M10 5c-1-2-3-2-4-1" /></>,
    reports: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M9 14h6" /><path d="M9 18h4" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.1a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
    bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.9 4.9 1.4 1.4" /><path d="m17.7 17.7 1.4 1.4" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m4.9 19.1 1.4-1.4" /><path d="m17.7 6.3 1.4-1.4" /></>,
    moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></>,
    chevron: <path d="m6 9 6 6 6-6" />,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></>,
    messages: <><path d="M21 12a8 8 0 0 1-12 7l-5 1 1-4A8 8 0 1 1 21 12z" /></>,
    profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
}

const NAV = [
  { label: "Dashboard", to: "/dashboard", icon: "dashboard" as const },
  { label: "Clientes", to: "/clients", icon: "clients" as const },
  { label: "Mensajes", to: "/messages", icon: "messages" as const },
  { label: "Configuración", to: "/settings", icon: "settings" as const },
];

export function AppShell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [topbarSearch, setTopbarSearch] = useState("");
  const chevronRef = useRef<HTMLButtonElement>(null);
  const { theme, toggleTheme } = useThemeMode();

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!profileOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (chevronRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest(".sh2-profile-menu")) return;
      setProfileOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen]);

  const fullName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email?.split("@")[0] ||
    "Juan Carlos";
  const initial = fullName[0]?.toUpperCase() ?? "C";
  const roleLabel = user?.role === "ADMIN" ? "Administrador" : "Coach Principal";
  const profilePhotoSrc = user?.profile_photo_url ? `${apiBaseUrl}${user.profile_photo_url}` : null;

  function handleTopbarSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = topbarSearch.trim();
    if (!query) return;

    const normalized = query.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const params = new URLSearchParams({ q: query });

    if (location.pathname.startsWith("/messages") || /\b(mensaje|chat|conversacion)\b/.test(normalized)) {
      navigate(`/messages?${params.toString()}`);
      return;
    }

    if (location.pathname.startsWith("/clients") || /\b(cliente|client|persona|usuario)\b/.test(normalized)) {
      navigate(`/clients?${params.toString()}`);
      return;
    }

    navigate(`/clients?${params.toString()}`);
  }

  return (
    <div className={`sh2-layout coach-platform${menuOpen ? "" : " sh2-layout--collapsed"}${mobileNavOpen ? " sh2-layout--drawer-open" : ""}`}>
      <button
        type="button"
        className="sh2-drawer-overlay"
        aria-label="Cerrar menú"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />
      <aside className="sh2-sidebar">
        <NavLink to="/dashboard" className="sh2-brand">
          <span className="sh2-brand-logo"><img src="/logos/jc-logo.png" alt="Jorge Carrillo" /></span>
        </NavLink>

        <nav className="sh2-nav" aria-label="Navegación principal del coach">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.to === "/dashboard"}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) => `sh2-nav-item${isActive ? " sh2-nav-item--active" : ""}`}
            >
              <span className="sh2-nav-icon"><Icon name={item.icon} /></span>
              <span className="sh2-nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <section className="sh2-promo" aria-label="Motivación">
          <div className="sh2-promo-body">
            <p className="sh2-promo-headline">Impulsa cada meta.</p>
            <p className="sh2-promo-sub">Tu guias. Ellos logran.</p>
          </div>
          <button className="sh2-promo-btn" type="button" aria-label="Crear rutina">
            <Icon name="routines" />
          </button>
          <img className="sh2-promo-figure" src="/gym-athlete-promo.jpeg" alt="" />
        </section>
      </aside>

      <div className="sh2-body">
        <header className="sh2-topbar">
          <div className="sh2-topbar-left">
            <button
              className="sh2-icon-btn"
              type="button"
              onClick={() => {
                if (window.matchMedia("(max-width: 760px)").matches) {
                  setMobileNavOpen((value) => !value);
                  return;
                }

                setMenuOpen((value) => !value);
              }}
              aria-label="Abrir o cerrar menú"
            >
              <Icon name="menu" />
            </button>
            <form className="sh2-search-wrap" onSubmit={handleTopbarSearch}>
              <span className="sh2-search-ico"><Icon name="search" /></span>
              <input
                className="sh2-search-input"
                type="search"
                placeholder="Buscar clientes, rutinas, check-ins..."
                value={topbarSearch}
                onChange={(event) => setTopbarSearch(event.target.value)}
              />
            </form>
          </div>

          <div className="sh2-topbar-right">
            <NotificationsDropdown trigger={<Icon name="bell" />} />
            <button
              className="sh2-icon-btn"
              type="button"
              aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              onClick={toggleTheme}
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} />
            </button>

            <div className="sh2-user-btn">
              <span className="sh2-topbar-avatar sh2-topbar-avatar--photo">
                {profilePhotoSrc ? (
                  <img src={profilePhotoSrc} alt={fullName} />
                ) : (
                  initial
                )}
              </span>
              <div className="sh2-user-meta">
                <strong>{fullName}</strong>
                <span>{roleLabel}</span>
              </div>
              <button
                ref={chevronRef}
                type="button"
                className="sh2-user-chevron"
                aria-label="Abrir menu de usuario"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((value) => !value)}
              >
                <Icon name="chevron" />
              </button>
              {profileOpen ? (
                <div className="sh2-profile-menu">
                  <NavLink to="/profile" className="sh2-profile-item" onClick={() => setProfileOpen(false)}>
                    <Icon name="profile" /> Mi perfil
                  </NavLink>
                  <NavLink to="/settings" className="sh2-profile-item" onClick={() => setProfileOpen(false)}>
                    <Icon name="settings" /> Configuración
                  </NavLink>
                  <button
                    className="sh2-profile-item"
                    type="button"
                    onClick={() => {
                      signOut();
                      setProfileOpen(false);
                    }}
                  >
                    <Icon name="logout" /> Cerrar sesión
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="sh2-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
